/**
 * SettingReadService.js
 * 
 * Service for reading setting data from Firestore.
 */

// Firebase
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Constants
const SETTINGS_COLLECTION = collections.settings;

const toSettingsRow = (docSnap) => {
  return docSnap.exists()
    ? { id: docSnap.id, ...(docSnap.data() || {}) }
    : null;
};

/**
 * Reads the settings document for a specific user once.
 *
 * @param {string} userKey - The user key to scope settings.
 *
 * @returns {Promise<object|null>} Settings row or null.
 */
export async function getSettings(userKey) {

  // Validate userKey
  if (!userKey) {
    return null;
  }

  const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, userKey));
  return toSettingsRow(docSnap);
}

/**
 * Subscribes to the settings document for a specific user.
 *
 * @param {string} userKey - The user key to scope settings.
 * @param {function} callback - Callback function to handle real-time updates.
 * @param {object} options - Optional handlers.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToSettings(userKey, callback, options = {}) {
  const { onError } = options;

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  // Validate userKey
  if (!userKey) {
    callback(null);
    return () => { };
  }

  // Subscribe to real-time updates
  return onSnapshot(
    doc(db, SETTINGS_COLLECTION, userKey),
    (docSnap) => {
      callback(toSettingsRow(docSnap));
    },
    (error) => {
      console.error('[SettingReadService] subscribeToSettings error:', error);

      if (typeof onError === 'function') {
        onError(error);
        return;
      }

      callback(null);
    }
  );
}
